import React, { useEffect, useState } from "react";
import axios from "axios";
import "./JuridentPayment.css";
import JuridentPaymentConfig from "./config";

const JuridentSuccess = () => {
  const [updateStatus, setUpdateStatus] = useState("Updating subscription...");
  const [isUpdating, setIsUpdating] = useState(true);
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    // Update Firebase subscription via Cloud Function after successful payment
    const updateFirebaseSubscription = async () => {
      try {
        const params = new URLSearchParams(window.location.search);

        // Check if environment mode is passed from Flutter app
        const isTestModeParam = params.get("isTestMode");
        if (isTestModeParam !== null) {
          // Override config with the mode passed from Flutter app
          JuridentPaymentConfig.isTestMode = isTestModeParam === 'true';
          console.log('🔧 [Config Override] Environment mode set from URL parameter:', isTestModeParam);
        }

        const userId = params.get('userId');
        const transactionId = params.get('transactionId');
        const amount = params.get('amount');
        const category = params.get('category');
        const phoneNo = params.get('phoneNo');
        const userName = params.get('userName');

        // Store payment data for display
        setPaymentData({
          amount: parseInt(amount),
          category,
          transactionId,
          userName: userName || 'User'
        });

        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('📤 UPDATING FIREBASE SUBSCRIPTION');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('User ID:', userId);
        console.log('Transaction ID:', transactionId);
        console.log('Amount (paise):', amount);
        console.log('Amount (rupees):', parseInt(amount) / 100);
        console.log('Category:', category);
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

        // Get Cloud Function URL from config
        const CLOUD_FUNCTION_URL = JuridentPaymentConfig.cloudFunctionUrl;
        console.log('🔧 [Config] Using configuration:', JuridentPaymentConfig.environmentName);
        console.log('🔧 [Config] Cloud Function URL:', CLOUD_FUNCTION_URL);

        // Convert amount back to rupees for Cloud Function (it expects rupees)
        const amountInRupees = parseInt(amount) / 100;

        const payload = {
          userId,
          transactionId,
          amount: amountInRupees,
          category,
          phoneNo,
          userName,
          status: 'success'
        };

        console.log('📤 Sending payload:', payload);

        const response = await axios.post(CLOUD_FUNCTION_URL, payload, {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 10000 // 10 second timeout
        });

        console.log('✅ Firebase updated successfully:', response.data);
        setUpdateStatus("Subscription activated! ✅");
        setIsUpdating(false);

      } catch (error) {
        console.error('❌ Error updating Firebase:', error);
        console.error('Error details:', error.response?.data || error.message);

        // Still show success to user (payment was successful)
        // Backend will handle subscription update retry if needed
        setUpdateStatus("Payment successful! Subscription will be activated shortly.");
        setIsUpdating(false);
      }
    };

    // Update subscription immediately
    updateFirebaseSubscription();
  }, []);

  return (
    <div className="jurident-payment-container">
      <div className="payment-card">
        {/* Environment Badge */}
        {JuridentPaymentConfig.isTestMode && (
          <div style={{
            background: '#ffc107',
            color: '#000',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '600',
            marginBottom: '15px',
            display: 'inline-block'
          }}>
            🧪 TEST MODE
          </div>
        )}

        {/* Success Animation */}
        <div className="success-icon">
          <svg
            width="100"
            height="100"
            viewBox="0 0 24 24"
            fill="none"
            stroke={JuridentPaymentConfig.colors.success}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ filter: "drop-shadow(0 4px 6px rgba(76, 175, 80, 0.3))" }}
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M9 12l2 2 4-4" />
          </svg>
        </div>

        {/* Success Title */}
        <h1 style={{
          color: JuridentPaymentConfig.colors.success,
          textAlign: "center",
          fontSize: "28px",
          fontWeight: "700",
          margin: "20px 0 10px 0"
        }}>
          Payment Successful!
        </h1>

        {/* Payment Details */}
        {paymentData && (
          <div style={{
            background: "#f8f9fa",
            borderRadius: "12px",
            padding: "20px",
            margin: "20px 0",
            border: "1px solid #e0e0e0"
          }}>
            <div style={{ marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: JuridentPaymentConfig.colors.textLight, fontSize: "14px" }}>Amount Paid:</span>
              <span style={{ color: JuridentPaymentConfig.colors.primary, fontSize: "24px", fontWeight: "700" }}>
                ₹{(paymentData.amount / 100).toFixed(2)}
              </span>
            </div>
            <div style={{ marginBottom: "12px", display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#666", fontSize: "14px" }}>Plan:</span>
              <span style={{ color: "#333", fontSize: "14px", fontWeight: "600" }}>
                {paymentData.category}
              </span>
            </div>
            <div style={{ marginBottom: "12px", display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#666", fontSize: "14px" }}>Transaction ID:</span>
              <span style={{
                color: "#666",
                fontSize: "12px",
                fontFamily: "monospace",
                background: "#fff",
                padding: "4px 8px",
                borderRadius: "4px"
              }}>
                {paymentData.transactionId}
              </span>
            </div>
          </div>
        )}

        {/* Update Status */}
        <div style={{
          background: isUpdating ? "#fff3cd" : "#d4edda",
          borderLeft: isUpdating ? "4px solid #ffc107" : "4px solid #4caf50",
          borderRadius: "4px",
          padding: "16px",
          marginBottom: "20px"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {isUpdating && (
              <div className="spinner" style={{
                border: "3px solid #f3f3f3",
                borderTop: "3px solid #ffc107",
                borderRadius: "50%",
                width: "24px",
                height: "24px",
                animation: "spin 1s linear infinite",
                flexShrink: 0
              }}></div>
            )}
            <p style={{
              color: isUpdating ? "#856404" : "#155724",
              margin: 0,
              fontSize: "14px",
              fontWeight: "500"
            }}>
              {updateStatus}
            </p>
          </div>
        </div>

        {/* Instructions */}
        <div style={{
          textAlign: "center",
          padding: "20px",
          background: JuridentPaymentConfig.colors.primaryLight + '20',
          borderRadius: "8px",
          marginBottom: "20px",
          border: `2px solid ${JuridentPaymentConfig.colors.primaryLight}`
        }}>
          <p style={{ color: JuridentPaymentConfig.colors.text, margin: 0, fontSize: "16px", fontWeight: "500", lineHeight: "1.6" }}>
            You can close this browser and go back to the Jurident app
          </p>
        </div>

        {/* Security Badge */}
        <div className="security-badge" style={{ marginTop: "20px" }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#4caf50"
            strokeWidth="2"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <span style={{ color: "#666" }}>Secured by PhonePe • Powered by Jurident</span>
        </div>
      </div>
    </div>
  );
};

export default JuridentSuccess;
